import React from "react";
import { Typography, Tag } from "antd";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import { ReactComponent as SchedulingImg } from "../../assets/scheduling.svg";
import { Link, useLocation } from "react-router-dom";

const { Title, Text } = Typography;

/**
 * Walkthrough component that displays event details and patient information.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.activeEvent - The active event object.
 * @param {Array} props.orders - An array of order objects.
 * @param {string} props.type - The type of the event.
 * @param {boolean} props.fromEmployee - Indicates if the component is accessed from an employee view.
 * @returns {JSX.Element} The rendered Walkthrough component.
 */
const Walkthrough = ({ activeEvent, orders, type, fromEmployee }) => {
  const location = useLocation();
  const isAdmin = location.pathname.includes("admin");

  const getDate = () => {
    if (activeEvent?.ok2go_info?.id && activeEvent.ok2go_info.date) {
      return moment(activeEvent.ok2go_info.date);
    }
    return activeEvent.ok2go_info?.id
      ? null
      : activeEvent.start
      ? moment(activeEvent.start)
      : null;
  };

  const getDay = () => {
    if (activeEvent.ok2go_info?.id) {
      return activeEvent.ok2go_info?.date || activeEvent.ok2go_info?.exit_date;
    }
    return activeEvent.start;
  };

  const date = getDate();
  const day = getDay();
  const start = activeEvent && date ? date.format("HH:mm") : "";
  const getEndTime = () => {
    if (activeEvent?.ok2go_info?.id && activeEvent?.ok2go_info?.exit_date) {
      return moment(activeEvent.ok2go_info.exit_date).format("HH:mm");
    }
    return activeEvent?.ok2go_info?.id
      ? ""
      : activeEvent.end
      ? moment(activeEvent.end).format("HH:mm")
      : "";
  };

  const end = getEndTime();

  const getDuration = () => {
    if (activeEvent && activeEvent.start && activeEvent.end) {
      return moment
        .duration(moment(activeEvent.end).diff(moment(activeEvent.start)))
        .asHours();
    }
    return "";
  };

  const duration = getDuration();

  const getOrder = () => {
    if (orders && activeEvent) {
      return orders.find((i) => i.id === activeEvent.order_id);
    }
    return null;
  };

  const order = getOrder();

  const getLinkPath = () => {
    if (isAdmin && order && order.patient) {
      return `/admin/patients/${order.patient.id}`;
    }
    if (order && order.patient) {
      return `/branches/${order.patient.branch}/patients/${order.patient.id}`;
    }
    return "";
  };

  const linkPath = getLinkPath();

  return (
    <div className="walkthrough">
      <div className="inner">
        <div className="imgContainer">
          <SchedulingImg className="img" />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ marginBottom: "0.5em" }}>
            <Title level={2} style={{ margin: 0 }}>
              {day && moment(day).format("dddd")}
            </Title>
            {type !== "plan" && type !== "replacements" && day && (
              <Text type="secondary">{moment(day).format("DD/MM/YYYY")}</Text>
            )}
          </div>
          {activeEvent?.ok2go_info?.id && (
            <div style={{ marginTop: "0.6em" }}>
              <Tag color="processing" style={{ margin: "0" }}>
                <FormattedMessage id="app.gaps.ok2go" />
              </Tag>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <Text type="secondary">
              <FormattedMessage id="app.plans.start" />
            </Text>
            <Title level={4}>{start}</Title>
          </div>
          {end && (
            <div>
              <Text type="secondary">
                <FormattedMessage id="app.plans.end" />
              </Text>
              <Title level={4}>{end}</Title>
            </div>
          )}
        </div>

        <Text type="secondary">
          <FormattedMessage id="app.plans.totalHours" />
        </Text>
        <Title level={4}>{Math.round(duration * 100) / 100}</Title>
        <Text type="secondary">
          <FormattedMessage id="app.plans.provider" />
        </Text>
        <Title level={4}>
          {activeEvent && order && order.client && order.client.category
            ? order.client.category.name
            : "-"}
        </Title>
        <Text type="secondary">
          <FormattedMessage id="app.plans.client" />
        </Text>
        <Title level={4}>
          {activeEvent && order && order.client ? order.client.name : "-"}
        </Title>
        <Text type="secondary">
          <FormattedMessage id="app.plans.patient" />
        </Text>
        <Link style={{ color: "inherit" }} to={{ pathname: linkPath }}>
          <Title style={{ textDecoration: "underline" }} level={4}>
            {activeEvent && order
              ? `${order.patient.surename} ${order.patient.first_name}`
              : "-"}
          </Title>
        </Link>
      </div>
    </div>
  );
};

export default Walkthrough;
